import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import PublicRiskAssessment from "@/pages/PublicRiskAssessment";
import OnboardingForm from "@/pages/OnboardingForm";
import SchedulingPage from "@/pages/SchedulingPage";
import PlanClientView from "@/pages/PlanClientView";
import ManageBooking from "@/pages/ManageBooking";
import PortalRequestLink from "@/pages/PortalRequestLink";
import PortalAccess from "@/pages/PortalAccess";
import PortalHome from "@/pages/PortalHome";
import MutualFundResearch from "@/pages/MutualFundResearch";
import ToolsPage from "@/pages/ToolsPage";
import BenchmarksAdmin from "@/pages/tools/BenchmarksAdmin";
import IntelligencePage from "@/pages/Intelligence";

import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import ProspectsList from "@/pages/ProspectsList";
import ClientsList from "@/pages/ClientsList";
import ClientWorkspace from "@/pages/ClientWorkspace";
import ExecutionQueue from "@/pages/ExecutionQueue";
import ReviewsQueue from "@/pages/ReviewsQueue";
import AvailabilitySettings from "@/pages/AvailabilitySettings";
import DiscoveryCallsQueue from "@/pages/DiscoveryCallsQueue";
import TeamMembers from "@/pages/TeamMembers";
import ProspectDetail from "@/pages/ProspectDetail";
import ChangePassword from "@/pages/ChangePassword";
import AuditLog from "@/pages/AuditLog";
import EmailTemplates from "@/pages/EmailTemplates";
import InviteAccept from "@/pages/InviteAccept";

const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const RequireRole = ({ roles, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            {/* Staff login is the landing page. Public/prospect entry points live at
                dedicated URLs — /book-consultation is only linked from the external
                marketing site, never from inside the portal. */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/book-consultation" element={<OnboardingForm />} />
            {/* Backward-compat redirects for any older links out in the wild */}
            <Route path="/onboard" element={<Navigate to="/book-consultation" replace />} />
            <Route path="/onboarding" element={<Navigate to="/book-consultation" replace />} />
            <Route path="/schedule/:token" element={<SchedulingPage />} />
            <Route path="/manage-booking/:token" element={<ManageBooking />} />
            <Route path="/risk-assessment/:token" element={<PublicRiskAssessment />} />
            <Route path="/plan-view/:token" element={<PlanClientView />} />
            <Route path="/client-login" element={<PortalRequestLink />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/my-portal" element={<Navigate to="/client-login" replace />} />
            <Route path="/my-portal/access/:token" element={<PortalAccess />} />
            <Route path="/my-portal/home" element={<PortalHome />} />

            <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="prospects" element={<RequireRole roles={["Admin", "Advisor", "ServiceRep"]}><ProspectsList /></RequireRole>} />
              <Route path="prospects/:id" element={<RequireRole roles={["Admin", "Advisor", "ServiceRep"]}><ProspectDetail /></RequireRole>} />
              <Route path="discovery-calls" element={<RequireRole roles={["Admin", "Advisor"]}><DiscoveryCallsQueue /></RequireRole>} />
              <Route path="settings/availability" element={<RequireRole roles={["Admin"]}><AvailabilitySettings /></RequireRole>} />
              <Route path="research/mutual-funds" element={<RequireRole roles={["Admin"]}><MutualFundResearch /></RequireRole>} />
              <Route path="research/benchmarks" element={<RequireRole roles={["Admin"]}><BenchmarksAdmin /></RequireRole>} />
              <Route path="intelligence" element={<RequireRole roles={["Admin"]}><IntelligencePage /></RequireRole>} />
              <Route path="tools" element={<RequireRole roles={["Admin", "Advisor"]}><ToolsPage /></RequireRole>} />
              <Route path="clients" element={<ClientsList />} />
              <Route path="clients/:id" element={<ClientWorkspace />} />
              <Route path="execution" element={<RequireRole roles={["Admin", "Advisor", "Execution"]}><ExecutionQueue /></RequireRole>} />
              <Route path="reviews" element={<RequireRole roles={["Admin", "Advisor"]}><ReviewsQueue /></RequireRole>} />
              <Route path="team" element={<RequireRole roles={["Admin"]}><TeamMembers /></RequireRole>} />
              <Route path="audit-log" element={<RequireRole roles={["Admin"]}><AuditLog /></RequireRole>} />
              <Route path="email-templates" element={<RequireRole roles={["Admin"]}><EmailTemplates /></RequireRole>} />
              <Route path="account" element={<ChangePassword />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
